import React, { useState, useEffect } from 'react'
import { Flex } from '../../Components/Common/Flex'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { columnProps } from '../../Services/Functions'
import { Button } from '../../Components/Common/Button'
import { useNavigate } from 'react-router-dom'
import { useCInfiniteQuery } from '../../Services/QueryCache'
import { Text } from '../../Components/Common/Text'
import { calculateDifference, deleteLineaOccupata } from './Functions'
import { Modal } from '../../Components/Common/Modal'
import { useAuth } from '../../Provider/AuthProvider'
import { Calendar } from 'primereact/calendar'
import { useCapsule } from '../../State/Functions'
import { cap } from '../../State'

export const getNestedValue = (obj, path) => path?.split('.')?.reduce((acc, part) => acc?.[part], obj)

export const LineaOccupataList = () => {
  const [range, setRange] = useCapsule(cap.lineaOccupataRange)
  const [datesSearch, setDatesSearch] = useState(range)
  const { data: lineaOccupata = [] } = useCInfiniteQuery(['lineaOccupataList', range])
  const [idToDelete, setIdToDelete] = useState('')
  const { user = {} } = useAuth()
  const { user: userObj = {} } = user || {}
  const { role, id: userId } = userObj
  const [tableState, setTableState] = useCapsule(cap.lineaOccupataDatatableState)

  const [hoursSum, setHoursSum] = useState('')

  useEffect(() => {
    const timeSum = lineaOccupata // filter with datatable filters
      ?.filter((item) =>
        Object.keys(tableState.filters).every((key) => {
          const filterValue = tableState?.filters?.[key]?.value
          if (filterValue) {
            const itemValue = getNestedValue(item, key)
            return itemValue?.toLowerCase()?.includes(filterValue?.toLowerCase())
          }
          return true
        }))
      ?.reduce((acc, curr) => {
        const startDate = new Date(curr.startDate)
        const endDate = new Date(curr.endDate)
        const diff = endDate - startDate
        return acc + diff
      }, 0)
    const hours = Math.floor(timeSum / 1000 / 60 / 60)
    const minutes = Math.floor((timeSum / 1000 / 60) % 60)
    setHoursSum(hours?.toString().padStart(2, '0') + ':' + minutes?.toString().padStart(2, '0'))
  }, [lineaOccupata?.length, tableState.filters])

  const [modalVisible, setModalVisible] = useState(false)

  const navigate = useNavigate()

  const BtnActionProc = ({ messagesToRead, statusLabel, id, user }) => (
    <Flex row style={{ width: '100%' }}>
      <Button round icon='fa-duotone fa-pen' style={{ marginRight: 10 }} onClick={() => navigate('/lineaoccupata/' + id)} />
      <Button
        round tooltip='Elimina' icon='fa-duotone fa-trash' onClick={() => {
          setIdToDelete(id)
          setModalVisible(true)
        }}
        disabled={user.id !== userId && role !== 'admin'}
      />
    </Flex>
  )

  /**
   * Elabora la descrizione dell'attività basandosi sui dati della riga fornita.
   * Prima cerca la label dalla relazione motivazioniAttivita, altrimenti usa il campo reason.
   *
   * @param {Object} rowData - Dati della riga contenenti la motivazione dell'attività.
   * @returns {string} - La label della motivazione o il codice reason come fallback.
   * @author @mranocchia
   */
  const elaborateDescriptionAttivita = (rowData) => {
    // Se esiste la relazione con motivazioniAttivita, usa quella
    if (rowData?.motivazioniAttivitum?.label) {
      return rowData.motivazioniAttivitum.label
    }
    // Fallback al campo reason per retrocompatibilità
    return rowData?.reason || ''
  }

  return (
    <Flex fw>
      <Modal visible={modalVisible} header='Cancellazione attività' onHide={() => setModalVisible(false)}>
        <Flex fw>
          <Text value='Vuoi cancellare questa attività?' />
          <Flex fw row jb style={{ marginTop: 20 }}>
            <Button label='Annulla' icon='fa-duotone fa-xmark' onClick={() => setModalVisible(false)} />
            <Button
              label='Conferma' icon='fa-duotone fa-check' onClick={() => {
                deleteLineaOccupata(idToDelete)
                setModalVisible(false)
              }}
            />
          </Flex>
        </Flex>
      </Modal>
      <Flex fw jb row>
        <div style={{ width: '20%' }} />
        <Text value='Attività' size={22} bold />
        <Flex ae style={{ width: '20%' }}>
          <Button label='Aggiungi' icon='fa-duotone fa-plus' onClick={() => navigate('/lineaoccupata/new')} />
        </Flex>
      </Flex>
      <Flex fw row style={{ marginTop: 10 }}>
        <Flex style={{ width: '50%', marginLeft: '25%' }}>
          <Calendar
            hideOnRangeSelection
            value={datesSearch} selectionMode='range' onChange={(e) => {
              setDatesSearch(e.value)
              e.value?.filter(Boolean).length === 2 && setRange(e.value)
            }}
            locale='it' dateFormat='dd/mm/yy' style={{ width: 250 }}
          />
        </Flex>
        <Flex je row style={{ width: '25%' }}>
          {hoursSum && <Text value={'Totale ore ' + hoursSum} bold size={18} />}
        </Flex>
      </Flex>
      <DataTable
        style={{ width: '100%', marginTop: 20 }} filterDisplay='row' className='TableSignatures' sortField='startDate' sortOrder={1}
        value={lineaOccupata} emptyMessage='Nessuna attività trovata' responsiveLayout='scroll' paginator rows={10}
        first={tableState.first} onPage={setTableState}
        onFilter={(event) => { event.first = 0; setTableState(event) }}
        filters={tableState.filters} totalRecords={lineaOccupata.length}
      >
        {role === 'admin' || role === 'gestoreH24' || role === 'gestoreOperatori' || role === 'gestoreAttivita' ? <Column field='user.username' {...columnProps('Utente')} /> : null}
        <Column field='startDate' {...columnProps('Data Inizio')} body={(rowData) => new Date(rowData?.startDate).toLocaleString('it-IT')} />
        <Column field='endDate' {...columnProps('Data Fine')} body={(rowData) => new Date(rowData?.endDate).toLocaleString('it-IT')} />
        <Column {...columnProps('Durata')} body={(rowData) => calculateDifference(rowData?.startDate, rowData?.endDate)} />
        <Column field='workFromHome' {...columnProps('Da casa')} body={(rowData) => rowData?.workFromHome ? 'Sì' : 'No'} />
        <Column field='recuperoReperibilita' {...columnProps('Recupero reperibilità')} body={(rowData) => rowData?.recuperoReperibilita ? 'Sì' : 'No'} />
        <Column field='reason' {...columnProps('Motivazione')} body={elaborateDescriptionAttivita} />
        <Column field='environment.agencyName' {...columnProps('Agenzia')} body={(rowData) => rowData?.environment?.agencyName} />
        <Column style={{ border: 'none', width: 220, height: 15, textAlign: 'center' }} body={(props) => <BtnActionProc {...props} />} />
      </DataTable>
    </Flex>
  )
}
